import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { useAuthUser } from '../../../contexts/useAuthUser';
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  onSnapshot,
  query,
  getDocs,
  deleteDoc,
  getDoc,
  setDoc,
  updateDoc,
  where,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
} from 'firebase/firestore';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import * as ImagePicker from 'expo-image-picker';
import { firestore, storage } from '../../../services/firebase';
import { hasProductAccess } from '../../../utils/accessUtils';
import {
  isLegacyCommunityPost,
  isVirtualGeneralChannelSelected,
  VIRTUAL_GENERAL_CHANNEL_ID,
  VIRTUAL_GENERAL_CHANNEL_NAME,
} from '../../../utils/communityChannelUtils';
import {
  appendCommunityPageDocs,
  countPlaceholders,
  postsPushedOutOfFirstPage,
  toggleLikeOnPosts,
} from '../../../utils/communityFeed';

const CommunityContext = createContext();

const POSTS_PAGE_SIZE = 10;

// Posts apagados nesta sessão. A tela do post tem o seu PRÓPRIO
// CommunityProvider: apagar lá não mexia nas páginas extras do feed (que não
// têm listener), e o post continuava na lista ao voltar.
const deletedPostIds = new Set();

const channelOrderKey = (id) =>
  id === VIRTUAL_GENERAL_CHANNEL_ID ? 'general' : id;

const toPost = (docSnap) => ({ id: docSnap.id, ...docSnap.data() });

const postsPageQuery = (afterDoc) =>
  afterDoc
    ? query(collection(firestore, 'posts'), orderBy('createdAt', 'desc'), startAfter(afterDoc), limit(POSTS_PAGE_SIZE))
    : query(collection(firestore, 'posts'), orderBy('createdAt', 'desc'), limit(POSTS_PAGE_SIZE));

// Contagens de um post: 1 leitura por agregação (até mil itens) + a curtida
// do próprio aluno, em vez de ler cada curtida e cada comentário.
const fetchPostCounts = async (postRef, uid) => {
  const likesRef = collection(postRef, 'likes');
  const [likes, comments, mine] = await Promise.all([
    getCountFromServer(likesRef),
    getCountFromServer(collection(postRef, 'comments')),
    uid ? getDocs(query(likesRef, where('userId', '==', uid), limit(1))) : Promise.resolve(null),
  ]);
  const myLikeDoc = mine?.docs?.[0];
  return countPlaceholders(
    likes.data().count,
    comments.data().count,
    myLikeDoc ? { id: myLikeDoc.id, ...myLikeDoc.data() } : null
  );
};

// Uma página do canal, como no mobile: os posts vêm por data (índice
// automático) e o canal é filtrado em memória, lendo o próximo lote até a
// página encher — filtrar por canal E ordenar por data no servidor exigiria um
// índice composto criado à mão em cada projeto.
const collectChannelPage = async (accept, initialDocs, afterDoc) => {
  let collected = [];
  let batch = initialDocs;
  let cursor = afterDoc ?? null;

  while (collected.length < POSTS_PAGE_SIZE) {
    if (!batch) {
      const snap = await getDocs(postsPageQuery(cursor));
      batch = snap.docs;
    }
    const { docs, pageFilledAtDoc } = appendCommunityPageDocs(collected, batch, POSTS_PAGE_SIZE, accept);
    collected = docs;
    if (pageFilledAtDoc) return { docs: collected, cursor: pageFilledAtDoc, hasMore: true };
    if (batch.length < POSTS_PAGE_SIZE) {
      return { docs: collected, cursor: batch[batch.length - 1] ?? cursor, hasMore: false };
    }
    cursor = batch[batch.length - 1];
    batch = undefined;
  }
  return { docs: collected, cursor, hasMore: false };
};

export const CommunityProvider = ({
  children,
  initialSelectedChannelId = null,
  focusPostId = null,
}) => {
  const [isPostModalVisible, setIsPostModalVisible] = useState(false);
  const [textContent, setTextContent] = useState('');
  const [image, setImage] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  // Sessão e documento `users/{uid}` vêm do AuthUserProvider (resolvidos no boot).
  const { user: authUserDoc, userId: authUserId, isAnonymous } = useAuthUser();
  const isUser = useMemo(
    () => (authUserDoc ? { id: authUserId, ...authUserDoc } : undefined),
    [authUserDoc, authUserId]
  );
  const [messageImageError, setMessageImageError] = useState(false);
  const [loadingCard, setLoadingCard] = useState(true);
  const [messageError, setMessageError] = useState(false);
  const [post, setPost] = useState([]);
  const [allChannels, setAllChannels] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [channelOrders, setChannelOrders] = useState({});

  const auth = getAuth();

  useEffect(() => {
    const ref = doc(firestore, 'communitySettings', 'channelOrder');
    const unsubscribe = onSnapshot(ref, (snap) => {
      setChannelOrders(snap.data()?.orders || {});
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!auth.currentUser?.uid) return;
    const channelsQuery = query(collection(firestore, 'communityChannels'));
    const unsubscribe = onSnapshot(channelsQuery, (snapshot) => {
      const fetchedChannels = snapshot.docs.map((channelDoc) => ({
        id: channelDoc.id,
        ...channelDoc.data(),
      }));
      const virtualChannel = {
        id: VIRTUAL_GENERAL_CHANNEL_ID,
        name: VIRTUAL_GENERAL_CHANNEL_NAME,
      };
      const withVirtual = [virtualChannel, ...fetchedChannels].sort(
        (a, b) =>
          (channelOrders[channelOrderKey(a.id)] ?? Number.MAX_SAFE_INTEGER) -
          (channelOrders[channelOrderKey(b.id)] ?? Number.MAX_SAFE_INTEGER)
      );
      setAllChannels(withVirtual);

      const availableChannels = isUser?.isAdmin
        ? withVirtual
        : withVirtual.filter((channel) => channel.id === VIRTUAL_GENERAL_CHANNEL_ID || hasProductAccess(channel, isUser || {}));

      setChannels(availableChannels);
      setSelectedChannelId((prevSelected) => {
        if (!availableChannels.length) return null;
        if (initialSelectedChannelId) {
          // Só aceita o canal vindo da navegação se o usuário tiver acesso a ele;
          // sem essa checagem um link direto abriria posts de canal restrito.
          const requested = availableChannels.find(
            (channel) => channel.id === initialSelectedChannelId
          );
          if (requested) {
            return requested.id;
          }
        }
        const stillExists = availableChannels.some((channel) => channel.id === prevSelected);
        return stillExists ? prevSelected : availableChannels[0].id;
      });
    });
    return () => unsubscribe();
  }, [isUser, initialSelectedChannelId, channelOrders]);

  // ── Feed ──────────────────────────────────────────────────────────────────
  //
  // Antes: listener em TODOS os posts (sem limite) e, por post, listeners nas
  // curtidas, nos comentários e nas respostas de cada comentário — recriados do
  // zero a cada mudança em qualquer post. Abrir a comunidade relia a base
  // inteira dela, e cada curtida nova era relida por quem estava com a tela
  // aberta.
  //
  // Agora, como no mobile: páginas de POSTS_PAGE_SIZE (só a primeira em tempo
  // real, para post novo aparecer no topo) e, por post, só as contagens e se o
  // aluno curtiu. Comentários e respostas são lidos só na tela do post (ver
  // "Post em foco").
  const uid = auth.currentUser?.uid;
  const firstPageRef = useRef([]); // posts da 1ª página (listener)
  const extraPagesRef = useRef([]); // posts trazidos por loadMorePosts
  const countsRef = useRef(new Map()); // postId -> { dataLikes, dataComments }
  const cursorRef = useRef(null);
  const acceptRef = useRef(() => true);
  const runIdRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const [hasMorePosts, setHasMorePosts] = useState(false);
  const [loadingMorePosts, setLoadingMorePosts] = useState(false);

  const withCounts = (feedPost) => {
    const counts = countsRef.current.get(feedPost.id);
    return {
      ...feedPost,
      dataLikes: counts?.dataLikes || [],
      dataComments: counts?.dataComments || [],
    };
  };

  const publishFeed = () => {
    const firstIds = new Set(firstPageRef.current.map((p) => p.id));
    setPosts(
      [
        ...firstPageRef.current,
        ...extraPagesRef.current.filter((p) => !firstIds.has(p.id)),
      ]
        .filter((p) => !deletedPostIds.has(p.id))
        .map(withCounts)
    );
  };

  // `force`: recontar posts já contados (ex.: ao voltar da tela do post).
  const hydrateCounts = async (postRefs, runId, { force = false } = {}) => {
    const pending = force ? postRefs : postRefs.filter((p) => !countsRef.current.has(p.id));
    if (!pending.length) return;
    const results = await Promise.all(
      pending.map(async (p) => {
        try {
          return [p.id, await fetchPostCounts(p.ref, uid)];
        } catch (error) {
          console.error('Erro ao contar curtidas/comentários do post:', error);
          return null;
        }
      })
    );
    results.filter(Boolean).forEach(([id, counts]) => countsRef.current.set(id, counts));
    if (runId === runIdRef.current) publishFeed();
  };

  useEffect(() => {
    // A tela do post carrega só o post (efeito abaixo).
    if (focusPostId) return undefined;

    const runId = ++runIdRef.current;
    firstPageRef.current = [];
    extraPagesRef.current = [];
    // Sem limpar, voltar a um canal já visto mostrava as contagens da 1ª
    // visita: `hydrateCounts` pula post que já tem contagem.
    countsRef.current.clear();
    cursorRef.current = null;
    setHasMorePosts(false);

    if (!uid) {
      setPosts([]);
      setLoadingCard(false);
      return undefined;
    }

    if (!selectedChannelId) {
      setPosts([]);
      setLoadingCard(true);
      return undefined;
    }

    const viewingGeneral = isVirtualGeneralChannelSelected(selectedChannelId);
    acceptRef.current = (docSnap) =>
      viewingGeneral
        ? isLegacyCommunityPost(docSnap.data())
        : docSnap.data()?.channelId === selectedChannelId;

    setPosts([]);
    setLoadingCard(true);

    // Emissões podem se sobrepor enquanto a página do canal é completada.
    let latestEmission = 0;
    const unsubscribe = onSnapshot(
      postsPageQuery(null),
      async (snapshot) => {
        // O listener olha os posts mais recentes de TODOS os canais. Se nada
        // do que mudou é deste canal, a página dele é a mesma: refazê-la custa
        // caro num canal com poucos posts, em que a varredura vai até o fim
        // da coleção.
        if (
          latestEmission > 0 &&
          !snapshot.docChanges().some((change) => acceptRef.current(change.doc))
        ) {
          return;
        }
        const emission = ++latestEmission;
        try {
          const page = await collectChannelPage(acceptRef.current, snapshot.docs, null);
          if (runId !== runIdRef.current || emission !== latestEmission) return;

          const nextFirst = page.docs.map(toPost);
          if (extraPagesRef.current.length || loadingMoreRef.current) {
            const known = new Set(extraPagesRef.current.map((p) => p.id));
            extraPagesRef.current = [
              ...postsPushedOutOfFirstPage(firstPageRef.current, nextFirst).filter(
                (p) => !known.has(p.id)
              ),
              ...extraPagesRef.current,
            ];
          }
          firstPageRef.current = nextFirst;
          // Depois de "carregar mais", o cursor é o da última página trazida.
          if (!extraPagesRef.current.length) {
            cursorRef.current = page.cursor;
            setHasMorePosts(page.hasMore);
          }
          publishFeed();
          setLoadingCard(false);
          hydrateCounts(page.docs, runId);
        } catch (error) {
          console.error('Erro ao carregar os posts da comunidade:', error);
          if (runId === runIdRef.current) setLoadingCard(false);
        }
      },
      (error) => {
        console.error('Erro no listener de posts da comunidade:', error);
        setLoadingCard(false);
      }
    );

    return () => {
      runIdRef.current += 1;
      unsubscribe();
    };
  }, [selectedChannelId, focusPostId, uid]);

  const loadMorePosts = async () => {
    if (focusPostId || !hasMorePosts || loadingMoreRef.current || !cursorRef.current) return;
    const runId = runIdRef.current;
    loadingMoreRef.current = true;
    setLoadingMorePosts(true);
    try {
      const page = await collectChannelPage(acceptRef.current, undefined, cursorRef.current);
      if (runId !== runIdRef.current) return;
      cursorRef.current = page.cursor;
      setHasMorePosts(page.hasMore);
      const known = new Set(extraPagesRef.current.map((p) => p.id));
      extraPagesRef.current = [
        ...extraPagesRef.current,
        ...page.docs.map(toPost).filter((p) => !known.has(p.id)),
      ];
      publishFeed();
      hydrateCounts(page.docs, runId);
    } catch (error) {
      console.error('Erro ao carregar mais posts:', error);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMorePosts(false);
    }
  };

  // Sem listeners nas curtidas/comentários, as contagens do feed só mudam
  // quando relidas. A tela chama isto ao voltar do post (onde o aluno pode
  // ter comentado): ~3 leituras por post já carregado.
  const refreshCounts = () => {
    if (focusPostId) return;
    // Já tira da tela o post apagado na tela do post, antes das contagens.
    publishFeed();
    const loaded = [...firstPageRef.current, ...extraPagesRef.current]
      .filter((p) => !deletedPostIds.has(p.id))
      .map((p) => ({
        id: p.id,
        ref: doc(firestore, 'posts', p.id),
      }));
    hydrateCounts(loaded, runIdRef.current, { force: true });
  };

  // ── Post em foco (tela de comentários) ────────────────────────────────────
  //
  // Só o post aberto: o documento, a contagem de curtidas e os comentários com
  // as respostas, em tempo real (comentário novo aparece na hora). Antes esta
  // tela montava o feed inteiro do canal, com todos os listeners, para achar
  // um post.
  useEffect(() => {
    if (!focusPostId) return undefined;
    if (!uid) {
      setPosts([]);
      setLoadingCard(false);
      return undefined;
    }

    const postRef = doc(firestore, 'posts', focusPostId);
    let active = true;
    let postData; // undefined = ainda não chegou; null = não existe
    let comments = [];
    let initialLikes = [];
    const repliesByComment = new Map();
    const replySubs = new Map();
    setLoadingCard(true);

    const publish = () => {
      if (!active || postData === undefined) return;
      if (postData === null) {
        setPosts([]);
        return;
      }
      const dataComments = comments.map((comment) => ({
        ...comment,
        replies: repliesByComment.get(comment.id) || [],
      }));
      // As curtidas não têm listener: mantém o que já está na tela (inclusive
      // o curtir/descurtir feito aqui).
      setPosts((prev) => {
        const previous = prev.find((p) => p.id === focusPostId);
        return [{ ...postData, dataLikes: previous ? previous.dataLikes : initialLikes, dataComments }];
      });
    };

    fetchPostCounts(postRef, uid)
      .then(({ dataLikes }) => {
        if (!active) return;
        initialLikes = dataLikes;
        setPosts((prev) => prev.map((p) => (p.id === focusPostId ? { ...p, dataLikes } : p)));
      })
      .catch((error) => console.error('Erro ao contar as curtidas do post:', error));

    const unsubscribePost = onSnapshot(
      postRef,
      (snap) => {
        postData = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        publish();
        setLoadingCard(false);
      },
      (error) => {
        console.error('Erro ao carregar o post:', error);
        setLoadingCard(false);
      }
    );

    // Um listener de respostas por comentário, criado uma vez e cancelado
    // quando o comentário some — não recriado a cada emissão.
    const unsubscribeComments = onSnapshot(collection(postRef, 'comments'), (snap) => {
      comments = snap.docs.map((commentDoc) => ({ id: commentDoc.id, ...commentDoc.data() }));
      const ids = new Set(comments.map((comment) => comment.id));
      replySubs.forEach((unsubscribe, commentId) => {
        if (ids.has(commentId)) return;
        unsubscribe();
        replySubs.delete(commentId);
        repliesByComment.delete(commentId);
      });
      comments.forEach((comment) => {
        if (replySubs.has(comment.id)) return;
        const repliesRef = collection(postRef, 'comments', comment.id, 'replies');
        replySubs.set(
          comment.id,
          onSnapshot(repliesRef, (repliesSnap) => {
            repliesByComment.set(
              comment.id,
              repliesSnap.docs.map((replyDoc) => ({ id: replyDoc.id, ...replyDoc.data() }))
            );
            publish();
          })
        );
      });
      publish();
    });

    return () => {
      active = false;
      unsubscribePost();
      unsubscribeComments();
      replySubs.forEach((unsubscribe) => unsubscribe());
    };
  }, [focusPostId, uid]);

  const openImagePicker = async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 1,
      });

      if (!result.canceled && result.assets?.[0]?.uri) {
        setImage(result.assets[0].uri);
        setMessageImageError(false);
      }
    } catch (error) {
      console.error('Erro ao abrir seletor de imagem:', error);
    }
  };

  const closeModal = () => {
    setIsPostModalVisible(false);
    setTextContent('');
    setImage(null);
    setLoadingCard(false);
    setLoading(false);
  };

  const handlePost = async () => {
    try {
      if (!selectedChannelId) return;
      const currentChannel = allChannels.find((channel) => channel.id === selectedChannelId);
      if (currentChannel?.onlyAdminCanPost && !isUser?.isAdmin) {
        return;
      }
      if (!textContent.trim() && !image) {
        return;
      }
      setLoading(true);
      if (image) {
        const imageRef = ref(
          storage,
          `community/${Math.random().toString(36).substring(2)}`
        );
        const response = await fetch(image);
        const blob = await response.blob();

        const uploadTask = uploadBytesResumable(imageRef, blob);

        uploadTask.on(
          'state_changed',
          null,
          (error) => {
            console.error('Erro durante o upload da imagem:', error);
            setMessageError(true);
            setLoading(false);
          },
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);

            await createPost(url);
          }
        );
      } else {
        await createPost(null);
      }
    } catch (error) {
      console.error('Erro ao criar o post:', error);
      setMessageError(true);
      setLoading(false);
    }
  };

  const createPost = async (imageUrl) => {
    try {
      const authorName =
        isUser?.Nome_Completo?.trim() ||
        isUser?.Email?.trim() ||
        auth.currentUser?.displayName?.trim() ||
        'User';

      const newPostData = {
        authorId: auth.currentUser.uid,
        contentText: textContent,
        createdAt: serverTimestamp(),
        nameUser: authorName,
      };

      if (!isVirtualGeneralChannelSelected(selectedChannelId)) {
        newPostData.channelId = selectedChannelId;
      }

      if (imageUrl) {
        newPostData.contentImage = imageUrl;
      }

      if (isUser.photo) {
        newPostData.photo = isUser.photo;
      }

      await addDoc(collection(firestore, 'posts'), newPostData);
      setLoading(false);
      closeModal();
    } catch (error) {
      console.error('Erro ao salvar o post no Firestore:', error);
      setMessageError(true);
    } finally {
      setLoading(false);
    }
  };


  const filterPostForId = (id) => {
    if (!id) {
      setPost([]);
      return [];
    }
    const filteredPost = posts.filter((post) => post.id === id);

    if (filteredPost.length > 0) {
      const validComments = filteredPost[0].dataComments.filter(
        (comment) => comment.createdAt && comment.createdAt.seconds
      );

      const sortedComments = validComments.sort(
        (a, b) => b.createdAt.seconds - a.createdAt.seconds
      );

      const updatedPost = { ...filteredPost[0], dataComments: sortedComments };

      setPost([updatedPost]);
      return [updatedPost];
    }

    setPost([]);
    return [];
  };

  const handleCommentSubmit = async (comment, setComment, postId) => {
    try {
      if (!comment.trim()) {
        return;
      }
      const commentData = {
        userId: auth.currentUser.uid,
        comment: comment,
        createdAt: serverTimestamp(),
        userName: isUser.Nome_Completo,
      };

      const commentRef = collection(
        doc(firestore, 'posts', postId),
        'comments'
      );
      await addDoc(commentRef, commentData);
      setComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleReplySubmit = async (parentCommentId, replyText, postId) => {
    try {
      if (!replyText.trim()) {
        return;
      }
      const replyData = {
        userId: auth.currentUser.uid,
        reply: replyText,
        createdAt: serverTimestamp(),
        likes: 0,
        commentId: parentCommentId,
      };

      const commentRef = doc(
        firestore,
        'posts',
        postId,
        'comments',
        parentCommentId
      );
      const repliesCollectionRef = collection(commentRef, 'replies');
      await addDoc(repliesCollectionRef, replyData);
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  };

  const isAdminOrIsUserCurrent = (id) => {
    if (isUser.id === id || isUser.isAdmin === true) {
      return true;
    }
    return false;
  };

  const handleDeleteComment = async (commentId, id) => {
    try {
      const postRef = doc(firestore, 'posts', id);

      const commentRef = doc(postRef, 'comments', commentId);

      const repliesQuerySnapshot = await getDocs(
        collection(commentRef, 'replies')
      );

      const deleteRepliesPromises = repliesQuerySnapshot.docs.map(
        async (replyDoc) => {
          await deleteDoc(replyDoc.ref);
        }
      );

      await Promise.all(deleteRepliesPromises);

      await deleteDoc(commentRef);
    } catch (error) {
      console.error('Erro ao excluir comentário e suas respostas:', error);
    }
  };

  const handleDeleteReply = async (commentId, replyId, id) => {
    try {
      const postRef = doc(firestore, 'posts', id);
      const commentRef = doc(postRef, 'comments', commentId);
      const replyRef = collection(commentRef, 'replies');
      const specificReplyRef = doc(replyRef, replyId);
      await deleteDoc(specificReplyRef);
    } catch (error) {
      console.error('Erro ao excluir resposta:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    // Antes do primeiro `await`: a tela do post chama isto sem esperar e já
    // navega para o feed, que filtra o post ao ganhar foco.
    deletedPostIds.add(postId);
    try {
      const likesRef = collection(firestore, 'posts', postId, 'likes');
      const likesQuerySnapshot = await getDocs(likesRef);
      likesQuerySnapshot.forEach(async (likeDoc) => {
        await deleteDoc(doc(firestore, 'posts', postId, 'likes', likeDoc.id));
      });

      const commentsRef = collection(firestore, 'posts', postId, 'comments');
      const commentsQuerySnapshot = await getDocs(commentsRef);
      commentsQuerySnapshot.forEach(async (commentDoc) => {
        const repliesRef = collection(
          firestore,
          'posts',
          postId,
          'comments',
          commentDoc.id,
          'replies'
        );

        const repliesQuerySnapshot = await getDocs(repliesRef);
        repliesQuerySnapshot.forEach(async (replyDoc) => {
          await deleteDoc(
            doc(
              firestore,
              'posts',
              postId,
              'comments',
              commentDoc.id,
              'replies',
              replyDoc.id
            )
          );
        });
        await deleteDoc(
          doc(firestore, 'posts', postId, 'comments', commentDoc.id)
        );
      });

      const postDocRef = doc(firestore, 'posts', postId);
      const postDocSnapshot = await getDoc(postDocRef);

      if (!postDocSnapshot.exists()) {
        console.error('Documento do post não encontrado');
        return;
      }

      const postData = postDocSnapshot.data();
      const imgUrl = postData.contentImage;

      if (imgUrl) {
        const imagePath = imgUrl.split('%2F')[1].split('?')[0];
        const storageRef = ref(storage, `community/${imagePath}`);
        await deleteObject(storageRef);
      } else {
        console.error('URL da imagem não encontrada no documento do post');
      }
      await deleteDoc(doc(firestore, 'posts', postId));
      // Posts de "carregar mais" não têm listener: sem isto o post voltaria
      // na próxima atualização do feed.
      extraPagesRef.current = extraPagesRef.current.filter((p) => p.id !== postId);
      countsRef.current.delete(postId);
    } catch (error) {
      deletedPostIds.delete(postId);
      console.error('Erro ao excluir o post:', error);
    }
  };

  const createChannel = async (channelData) => {
    if (!isUser?.isAdmin) return;
    await addDoc(collection(firestore, 'communityChannels'), {
      ...channelData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };

  const updateChannel = async (channelId, channelData) => {
    if (!isUser?.isAdmin) return;
    await updateDoc(doc(firestore, 'communityChannels', channelId), {
      ...channelData,
      updatedAt: serverTimestamp(),
    });
  };

  const moveChannel = async (channelId, direction) => {
    if (!isUser?.isAdmin) return;
    const idx = allChannels.findIndex((c) => c.id === channelId);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (idx === -1 || newIdx < 0 || newIdx >= allChannels.length) return;
    const reordered = [...allChannels];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    const orders = {};
    reordered.forEach((c, i) => { orders[channelOrderKey(c.id)] = i; });
    await setDoc(doc(firestore, 'communitySettings', 'channelOrder'), { orders });
  };

  const deleteChannel = async (channelId) => {
    if (!isUser?.isAdmin) return;
    const postsSnapshot = await getDocs(
      query(collection(firestore, 'posts'), where('channelId', '==', channelId))
    );

    for (const postDoc of postsSnapshot.docs) {
      await handleDeletePost(postDoc.id);
    }

    await deleteDoc(doc(firestore, 'communityChannels', channelId));
  };

  const handleLike = async (item, auth) => {
    const likerId = auth?.currentUser?.uid;
    if (!likerId) return;
    try {
      // A curtida do aluno vem do servidor (só a dele), não do array da tela:
      // antes, descurtir lia TODAS as curtidas do post para achar uma.
      const postRef = doc(firestore, 'posts', item.id);
      const likesRef = collection(postRef, 'likes');
      const mine = await getDocs(query(likesRef, where('userId', '==', likerId)));
      const hadLike = !mine.empty;

      if (hadLike) {
        // Apaga também duplicatas antigas: o estado volta a "sem curtida".
        await Promise.all(mine.docs.map((likeDoc) => deleteDoc(likeDoc.ref)));
      } else {
        await addDoc(likesRef, {
          userId: likerId,
          createdAt: serverTimestamp(),
          liked: true,
        });
      }

      // Sem listener nas curtidas: atualiza a tela e a contagem guardada.
      setPosts((prevPosts) => toggleLikeOnPosts(prevPosts, item.id, likerId, hadLike));
      const counts = countsRef.current.get(item.id);
      if (counts) {
        const [updated] = toggleLikeOnPosts([{ id: item.id, ...counts }], item.id, likerId, hadLike);
        countsRef.current.set(item.id, { ...counts, dataLikes: updated.dataLikes });
      }
    } catch (error) {
      console.error('Error updating like:', error);
    }
  };

  const selectedChannel = allChannels.find((channel) => channel.id === selectedChannelId);
  const canUserPostOnSelectedChannel =
    !!selectedChannel && (!selectedChannel.onlyAdminCanPost || isUser?.isAdmin);

  const contextValues = {
    isPostModalVisible,
    setIsPostModalVisible,
    textContent,
    setTextContent,
    image,
    setImage,
    posts,
    hasMorePosts,
    loadingMorePosts,
    loadMorePosts,
    refreshCounts,
    loading,
    isUser,
    openImagePicker,
    closeModal,
    handlePost,
    isAnonymous,
    messageError,
    setMessageError,
    messageImageError,
    loadingCard,
    setPosts,
    auth,
    filterPostForId,
    post,
    handleCommentSubmit,
    handleReplySubmit,
    isAdminOrIsUserCurrent,
    handleDeleteComment,
    handleDeleteReply,
    handleDeletePost,
    handleLike,
    channels,
    allChannels,
    selectedChannelId,
    setSelectedChannelId,
    createChannel,
    updateChannel,
    deleteChannel,
    moveChannel,
    canUserPostOnSelectedChannel,
  };

  return (
    <CommunityContext.Provider value={contextValues}>
      {children}
    </CommunityContext.Provider>
  );
};

export const useCommunity = () => useContext(CommunityContext);
